import { queryOptions, type QueryClient } from "@tanstack/react-query"
import { api, ApiError } from "./api"
import { isV2Api } from "./api-contract"
import { useEvents } from "./active-event"
import type { V2EventPerson } from "./v2/contracts"
import { v2Api } from "./v2/client"
import {
  getEventPerson,
  issueEventInvitation,
  listAllActivities,
  listAllActivityRegistrations,
  listAllEventPeople,
} from "./v2/organizer"

// Guest ("people") data layer for the Console collection view + the full guest
// record. Mirrors the admin GuestsPage / GuestProfilePage queries (event-scoped
// `/api/v1/guests`) and reuses the SAME cache key shapes admin uses
// (`["resource", "guests", { eventId }]`, `["guest", id]`) so the vendored
// realtime layer can patch them on live CRUD.

export type Guest = {
  id: string
  /** Durable v2 human identity; distinct from event-person id and BA user id. */
  personId?: string
  eventId: string
  organizationId: string
  linkedUserId?: string | null
  contactId?: string | null
  hostGuestId?: string | null
  relationshipToHost?: string | null
  firstName: string
  lastName?: string | null
  badgeFirstName?: string | null
  badgeLastName?: string | null
  nameAtInvite: string
  emailAtInvite: string
  phoneAtInvite?: string | null
  smsAlertsConsentAt?: string | null
  titleAtInvite?: string | null
  companyAtInvite?: string | null
  // Free-text note a public registrant leaves when requesting to join.
  responseNote?: string | null
  displayName?: string | null
  // GuestProfile uses dietaryRequests/accessibilityRequests/notes; the list
  // (GuestsPage) edits dietaryNotes/specialRequests. Keep both projections —
  // the record reads whichever the backend returns.
  dietaryNotes?: string | null
  specialRequests?: string | null
  dietaryRequests?: string | null
  accessibilityRequests?: string | null
  notes?: string | null
  preApproved: number
  status: string
  // Purchased ticket tier (paid ticketing), denormalised onto the guest by the
  // Stripe webhook. Read-only — set at purchase, never edited here. Null for
  // comped/invited guests or free events.
  ticketTypeId?: string | null
  ticketTypeName?: string | null
  guestCount: number
  maxGuests: number
  sentAt?: string | null
  respondedAt?: string | null
  checkedInAt?: string | null
  checkedOutAt?: string | null
  // Set when an organizer explicitly sent a calendar (ICS) invite; cleared on
  // cancel-calendar-invite. The signal that a calendar invite is outstanding.
  calendarInviteSentAt?: string | null
  createdAt: string
  modifiedAt?: string | null
}

type ListEnvelope<T> = { data: T[]; meta?: { count?: number } }

export const guestsKey = (eventId: string) =>
  ["resource", "guests", { eventId }] as const
export const guestKey = (guestId: string) => ["guest", guestId] as const

// ── @mention roster ──────────────────────────────────────────────────────────
// The event roster the "@" composer typeahead reads. Sourced from the
// purpose-built `list-roster` chat action (forDm:false), which returns confirmed
// attendees that have an @handle — the action's own description calls this the
// "@mention typeahead" mode. (Staff/team members are not in this roster; the
// action is the designated mention source and we reuse it rather than inventing
// a new endpoint.) Cached per-event so a workspace switch refetches.
export type MentionRosterPerson = {
  userId: string
  handle: string | null
  displayName: string
  avatarImageId: string | null
  canDm: boolean
}

export const mentionRosterKey = (eventId: string) =>
  ["mention-roster", eventId] as const

export function mentionRosterQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: mentionRosterKey(eventId),
    queryFn: () => isV2Api
      ? listAllEventPeople(eventId).then((people) => ({
          success: true as const,
          roster: people
            .filter((person) => person.status === "active")
            .map((person): MentionRosterPerson => ({
              userId: person.personId,
              handle: null,
              displayName: person.displayName,
              avatarImageId: null,
              canDm: true,
            })),
        }))
      : api<{ success: true; roster: MentionRosterPerson[] }>(
          `/api/v1/event/${eventId}/chat/list-roster`,
          { method: "POST", body: { forDm: false } },
        ),
    select: (r) => r.roster,
    staleTime: 60_000,
  })
}

export function guestsQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: guestsKey(eventId),
    queryFn: async () => {
      if (isV2Api) {
        const organizationId = useEvents.getState().byId(eventId)?.organizationId ?? ""
        const people = await listAllEventPeople(eventId)
        return { data: people.map((person) => eventPersonToGuest(person, organizationId)) }
      }
      return api<ListEnvelope<Guest>>("/api/v1/guests", {
        searchParams: { limit: 500, eventId, sort: "createdAt", order: "desc" },
      })
    },
    select: (r) => r.data,
  })
}

// ── single guest record + its satellite data ─────────────────────────────────

export function guestQueryOptions(eventId: string, guestId: string) {
  return queryOptions({
    queryKey: guestKey(guestId),
    queryFn: async () => {
      if (isV2Api) {
        const person = await getEventPerson(eventId, guestId)
        const organizationId = useEvents.getState().byId(eventId)?.organizationId ?? ""
        return eventPersonToGuest(person, organizationId)
      }
      return api<Guest>(`/api/v1/guests/${guestId}`)
    },
  })
}

export function eventPersonToGuest(person: V2EventPerson, organizationId: string): Guest {
  const registration = person.registration
  return {
    id: person.id,
    personId: person.personId,
    eventId: person.eventId,
    organizationId,
    hostGuestId: person.hostEventPersonId,
    relationshipToHost: person.relationshipToHost,
    firstName: person.firstName,
    lastName: person.lastName,
    badgeFirstName: person.badgeNamePreference.firstName,
    badgeLastName: person.badgeNamePreference.lastName,
    nameAtInvite: person.displayName,
    emailAtInvite: person.email,
    phoneAtInvite: person.phone ?? null,
    titleAtInvite: person.title,
    companyAtInvite: person.company,
    notes: person.notes ?? null,
    displayName: person.displayName,
    preApproved: registration?.status === "confirmed" ? 1 : 0,
    status: registration?.status ?? person.status,
    ticketTypeId: registration?.ticketType?.id ?? null,
    ticketTypeName: registration?.ticketType?.name ?? null,
    guestCount: 0,
    maxGuests: 0,
    sentAt: registration?.invitedAt ?? null,
    respondedAt: registration?.respondedAt ?? null,
    createdAt: person.createdAt,
  }
}

// ── structured dietary (first-class, event-scoped) ───────────────────────────
export type DietaryAllergy = { kind: string; label?: string | null; anaphylaxis?: boolean }
export type DietaryIntolerance = { kind: string; label?: string | null }
export type DietaryFlags = {
  isVegetarian: boolean
  isVegan: boolean
  isPescatarian: boolean
  isKosher: boolean
  isHalal: boolean
  isGlutenFree: boolean
  isDiabetic: boolean
  isLowSodium: boolean
}
export type GuestDietary = {
  success: true
  guestId: string
  shared: boolean
  flags?: DietaryFlags
  allergens?: DietaryAllergy[]
  intolerances?: DietaryIntolerance[]
  notes?: string | null
  anaphylaxis?: boolean
  source?: string | null
  updatedAt?: string | null
}

export const guestDietaryKey = (guestId: string) => ["guest-dietary", guestId] as const

// Canonical allergen/intolerance taxonomy (EU-14 + FDA-9). Cached app-wide.
export type AllergenCatalogEntry = {
  value: string
  label: string
  group?: string
  regulatory: string[]
}
export type DietaryTaxonomy = {
  success: true
  taxonomy: {
    allergens: { kinds: string[]; catalog: AllergenCatalogEntry[]; severities: string[] }
    intolerances: { kinds: { value: string; label: string }[] }
  }
}

export function dietaryTaxonomyQueryOptions() {
  return queryOptions({
    queryKey: ["dietary-taxonomy"] as const,
    staleTime: 1000 * 60 * 60, // taxonomy rarely changes
    queryFn: () => api<DietaryTaxonomy>("/api/v1/me/preferences/taxonomy"),
  })
}

// Staff write of a guest's structured dietary (setGuestDietary).
export function setGuestDietary(body: {
  guestId: string
  isVegetarian?: number
  isVegan?: number
  isPescatarian?: number
  isKosher?: number
  isHalal?: number
  isGlutenFree?: number
  isDiabetic?: number
  isLowSodium?: number
  allergens?: DietaryAllergy[] | null
  intolerances?: DietaryIntolerance[] | null
  notes?: string | null
}) {
  return api<{ success: true; guestId: string; updatedAt: string }>(
    "/api/v1/guests/set-dietary",
    { method: "POST", body },
  )
}

export function guestDietaryQueryOptions(guestId: string) {
  return queryOptions({
    queryKey: guestDietaryKey(guestId),
    queryFn: () => api<GuestDietary>("/api/v1/guests/get-dietary", {
        method: "POST",
        body: { guestId },
      }),
  })
}

// ── event-wide dietary report (the whole-trip rollup) ────────────────────────
export type DietaryReport = {
  success: true
  eventId: string
  total: number
  sharedCount: number
  notSharedCount: number
  plateHeadcount: number
  tagCounts: Record<string, number>
  allergenCounts: { kind: string; group: string | null; total: number; anaphylaxisCount: number }[]
  allergenGroupCounts: { group: string; total: number; anaphylaxisCount: number }[]
  customAllergens: { label: string; total: number; anaphylaxisCount: number }[]
  intoleranceCounts: { kind: string; total: number }[]
  customIntolerances: { label: string; total: number }[]
  anaphylaxisFlags: { guestId: string; displayName: string; kinds: string[]; notes: string | null }[]
  notShared: { guestId: string; displayName: string; email: string | null }[]
  guests: {
    guestId: string
    displayName: string
    status?: string | null
    shared: boolean
    dietary: {
      flags: DietaryFlags
      allergens: DietaryAllergy[]
      intolerances: DietaryIntolerance[]
      notes: string | null
    } | null
    source?: string | null
    updatedAt?: string | null
  }[]
}

export function eventDietaryReportQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: ["event-dietary-report", eventId] as const,
    queryFn: () => api<DietaryReport>("/api/v1/guests/dietary-report", {
        method: "POST",
        body: { eventId },
      }),
  })
}

// Diet-flag display labels (the 8 lifestyle/medical flags).
// Client mirror of the server-side canonical DIETARY_TAGS
// (quickback/features/user-preferences/lib/dietary-taxonomy.ts). Kept as a
// static list because consumers here (report export, participant tags) read it
// synchronously; the taxonomy endpoint doesn't ship the diet tags. If the
// canonical list changes, update this to match — field ⇄ label, in order.
export const DIET_FLAG_LABELS: { field: keyof DietaryFlags; label: string }[] = [
  { field: "isVegetarian", label: "Vegetarian" },
  { field: "isVegan", label: "Vegan" },
  { field: "isPescatarian", label: "Pescatarian" },
  { field: "isKosher", label: "Kosher" },
  { field: "isHalal", label: "Halal" },
  { field: "isGlutenFree", label: "Gluten-free" },
  { field: "isDiabetic", label: "Diabetic" },
  { field: "isLowSodium", label: "Low sodium" },
]

// Render an allergen/intolerance entry's human label (custom uses its label).
export function dietaryEntryLabel(e: { kind: string; label?: string | null }): string {
  if (e.kind === "other") return e.label || "Other"
  // Known kinds: prettify the snake_case value (taxonomy catalog has nicer
  // labels, but this is fine for chips without fetching the taxonomy).
  return e.kind.replace(/_/g, " ")
}

export type AgendaItem = {
  id: string
  name: string
  description?: string | null
  startsAt?: string | null
  endsAt?: string | null
  locationId?: string | null
  rsvpMode?: string | null
  groupId?: string | null
  // Present on the agenda-items list payload; optional here so the schedule
  // panel can show Required/Invite-only eyebrows + cancelled treatment.
  status?: string | null
  isRequired?: number | null
  accessMode?: string | null
  visibility?: string | null
}

export type AgendaRsvp = {
  id: string
  guestId: string
  agendaItemId: string
  status: string
  preApproved?: number | null
  createdAt?: string | null
}

export type CommsLog = {
  id: string
  guestId?: string | null
  channel: string
  category?: string | null
  subjectSnapshot?: string | null
  status: string
  sentAt?: string | null
  failedAt?: string | null
  triggeredByAction?: string | null
  createdAt?: string | null
}

export type FieldOption = { value: string; label: string }
export type AttendeeField = {
  id: string
  key: string
  label: string
  type: string
  options: FieldOption[] | null
  visibility: "admin_only" | "members_only" | "directory"
}
export type AttendeeGroup = {
  id: string
  name: string
  description: string | null
  visibility: "admin_only" | "members_only" | "directory"
  isAutomatic: boolean
  memberCount: number
}
export type AttendeeSetupResponse = {
  success: boolean
  fields?: AttendeeField[]
  groups?: AttendeeGroup[]
}
export type AttendeeFieldValue = {
  id: string
  fieldId: string
  fieldKey: string
  value: string | null
  source: string
  updatedAt?: string | null
}
export type AttendeeGroupMember = {
  id: string
  groupId: string
  guestId: string
  source: "rule" | "manual"
}

export type FieldSpec = {
  id: string
  type: string
  label: string
  options?: Array<{ value: string; label: string }>
  mode?: "date" | "datetime" | "time"
  maxRating?: number
}
export type GuestForm = {
  id: string
  eventId: string
  audience: "event" | "agenda_item" | "agenda_group" | "individual"
  agendaItemId: string | null
  agendaGroupId: string | null
  title: string
  body: string | null
  createdAt: string
  completedAt: string | null
  fields: FieldSpec[] | null
  response: Record<string, unknown> | null
  responsePolicy: "editable" | "one_time" | null
  flavor: "generic" | "important"
  responseVisibility: "self" | "private" | null
  // Official / system-form marker (e.g. "emergency_contact"). Null = ordinary.
  systemKey?: string | null
}

export type SmsRecipientEligibility = {
  guestId: string
  hasConsent: boolean
  consentAt: string | null
  suppressed: boolean
  suppressedAt: string | null
}

export function guestRsvpsQueryOptions(eventId: string, guestId: string) {
  return queryOptions({
    queryKey: ["guest-rsvps", guestId] as const,
    queryFn: async () => {
      if (isV2Api) {
        const activities = await listAllActivities(eventId)
        const registrationPages = await Promise.all(
          activities.map((activity) => listAllActivityRegistrations(eventId, activity.id)),
        )
        return {
          data: registrationPages.flatMap((page) =>
            page.registrations
              .filter((registration) => registration.eventPersonId === guestId)
              .map((registration): AgendaRsvp => ({
                id: registration.id,
                guestId: registration.eventPersonId,
                agendaItemId: registration.activityId,
                status: registration.status,
                preApproved: registration.status === "confirmed" ? 1 : 0,
                createdAt: registration.createdAt,
              })),
          ),
        }
      }
      return api<ListEnvelope<AgendaRsvp>>("/api/v1/agenda-rsvps", {
        searchParams: { guestId, limit: 200 },
      })
    },
    select: (r) => r.data,
  })
}

export function agendaItemsQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: ["agenda-items", eventId] as const,
    queryFn: async () => {
      if (isV2Api) {
        const activities = await listAllActivities(eventId)
        return {
          data: activities.map((activity): AgendaItem => ({
            id: activity.id,
            name: activity.name,
            description: activity.description,
            startsAt: activity.startsAt,
            endsAt: activity.endsAt,
            status: activity.status,
          })),
        }
      }
      return api<ListEnvelope<AgendaItem>>("/api/v1/agenda-items", {
        searchParams: { eventId, limit: 200 },
      })
    },
    select: (r) => r.data,
  })
}

export function guestCommsQueryOptions(guestId: string) {
  return queryOptions({
    queryKey: ["guest-comms", guestId] as const,
    queryFn: () => api<ListEnvelope<CommsLog>>("/api/v1/comms-log", {
        searchParams: { guestId, limit: 50 },
      }),
    select: (r) => r.data,
  })
}

export function guestFormsQueryOptions(eventId: string, guestId: string) {
  return queryOptions({
    queryKey: ["guest-forms", eventId, guestId] as const,
    queryFn: () => api<{ forms: GuestForm[] }>(
        `/api/v1/event/${eventId}/guests/${guestId}/forms`,
        { method: "POST", body: { guestId } },
      ),
    select: (r) => r.forms,
  })
}

// STAFF set/edit a guest's response to a form-todo (emergency contact,
// accessibility, …). Backed by the admin/member `set-guest-response` action;
// the server cleans to visible known fields + upserts the assignment row.
export async function setGuestResponse(input: {
  eventId: string
  todoId: string
  guestId: string
  response: Record<string, unknown>
  completed?: boolean
}): Promise<{ success: boolean; completedAt: string | null }> {
  return api(`/api/v1/event/${input.eventId}/todos/${input.todoId}/set-guest-response`, {
    method: "POST",
    body: {
      todoId: input.todoId,
      guestId: input.guestId,
      response: input.response,
      completed: input.completed ?? true,
    },
  })
}

export function attendeeSetupQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: ["attendee-fields-setup", eventId, "guest-overview"] as const,
    queryFn: () => api<AttendeeSetupResponse>(`/api/v1/event/${eventId}/attendee-fields/setup`, {
        method: "POST",
        body: {},
      }),
  })
}

export function attendeeFieldValuesQueryOptions(eventId: string, guestId: string) {
  return queryOptions({
    queryKey: ["guest-attendee-field-values", eventId, guestId] as const,
    queryFn: () => api<ListEnvelope<AttendeeFieldValue>>("/api/v1/attendee-field-values", {
        searchParams: { eventId, guestId, limit: 100 },
      }),
    select: (r) => r.data,
  })
}

// Set (or clear) a guest's attendee custom-field value. The action re-evaluates
// the guest's automatic-group memberships, so callers should invalidate BOTH
// the field-values and group-members queries on success.
export function setAttendeeFieldValue(
  eventId: string,
  input: { guestId: string; fieldId: string; value?: unknown; clear?: boolean },
) {
  return api<{ committed: boolean; guestId: string; fieldId: string }>(
    `/api/v1/event/${eventId}/attendee-fields/set-value`,
    { method: "POST", body: input },
  )
}

export function attendeeGroupMembersQueryOptions(eventId: string, guestId: string) {
  return queryOptions({
    queryKey: ["guest-attendee-group-members", eventId, guestId] as const,
    queryFn: () => api<ListEnvelope<AttendeeGroupMember>>("/api/v1/attendee-group-members", {
        searchParams: { eventId, guestId, limit: 100 },
      }),
    select: (r) => r.data,
  })
}

export function smsEligibilityQueryOptions(eventId: string, guestId: string) {
  return queryOptions({
    queryKey: ["sms-eligibility", eventId, guestId] as const,
    queryFn: () => api<{ recipients: SmsRecipientEligibility[] }>("/api/v1/comms/sms-eligibility", {
        method: "POST",
        body: { eventId, guestIds: [guestId] },
      }),
    select: (r) => r.recipients.find((rec) => rec.guestId === guestId) ?? null,
  })
}

// Plus-ones attached to a host guest.
export type PlusOneRow = {
  id: string
  firstName?: string | null
  lastName?: string | null
  displayName?: string | null
  nameAtInvite?: string | null
  emailAtInvite: string
  status: string
  relationshipToHost?: string | null
  sentAt?: string | null
}

export function plusOnesQueryOptions(eventId: string, hostGuestId: string) {
  return queryOptions({
    queryKey: ["guest-plus-ones", hostGuestId] as const,
    queryFn: async () => {
      if (isV2Api) {
        const people = await listAllEventPeople(eventId)
        return {
          data: people
            .filter((person) => person.hostEventPersonId === hostGuestId)
            .map((person): PlusOneRow => ({
              id: person.id,
              firstName: person.firstName,
              lastName: person.lastName,
              displayName: person.displayName,
              nameAtInvite: person.displayName,
              emailAtInvite: person.email,
              status: person.registration?.status ?? person.status,
              relationshipToHost: person.relationshipToHost,
              sentAt: person.registration?.invitedAt ?? null,
            })),
        }
      }
      return api<ListEnvelope<PlusOneRow>>("/api/v1/guests", {
        searchParams: { eventId, hostGuestId, limit: 100 },
      })
    },
    select: (r) => r.data,
  })
}

// ── guest record actions ─────────────────────────────────────────────────────

/** POST a record-scoped guest action (send-invite, check-in, cancel-invite…). */
export function guestAction(guestId: string, action: string, body: unknown = {}) {
  return api<Record<string, unknown>>(`/api/v1/guests/${guestId}/${action}`, {
    method: "POST",
    body,
  })
}

/** What `issueHandoutCode` returns — a revocable, expiring organizer-handout
 *  invite code minted WITHOUT emailing, for a printed QR / Slack handoff. The
 *  same `(email, code)` credential redeems via either acceptInvite (BA account)
 *  or enterEvent (sessionless) — `inviteUrl` is the string the apps' QR
 *  scanners decode. */
export type HandoutCode = {
  success?: boolean
  code: string
  codeDisplay: string
  inviteUrl: string
  email: string
  expiresAt: string
  issuedAt?: string
}

/** Get this guest's live handout code, minting one only if none exists
 *  (revocable, expiring; default 30-day TTL, server-capped at 120). Does NOT
 *  email — returns the code + QR url to show. Pass forceNew to rotate to a
 *  fresh code. */
export function issueHandoutCode(
  guestId: string,
  opts: { ttlDays?: number; forceNew?: boolean } = {},
) {
  return api<HandoutCode>(`/api/v1/guests/${guestId}/issue-handout-code`, {
    method: "POST",
    body: {
      ...(opts.ttlDays != null ? { ttlDays: opts.ttlDays } : {}),
      ...(opts.forceNew ? { forceNew: true } : {}),
    },
  })
}

/** Revoke all of this guest's live handout codes (the printed placard is dead). */
export function revokeHandoutCodes(guestId: string) {
  return api<{ success?: boolean; revoked?: number }>(
    `/api/v1/guests/${guestId}/revoke-handout-codes`,
    { method: "POST", body: {} },
  )
}

export function patchGuest(guestId: string, patch: Partial<Guest>) {
  return api<Record<string, unknown>>(`/api/v1/guests/${guestId}`, {
    method: "PATCH",
    body: patch,
  })
}

export function deleteGuest(guestId: string) {
  return api<null>(`/api/v1/guests/${guestId}`, { method: "DELETE" })
}

// ── lifecycle action registry + status gating ────────────────────────────────
// Single source of truth so the People list rows + bulk bar + the GuestRecord
// header/footer all agree on which lifecycle actions a guest's status permits,
// and on the endpoint + success copy + optimistic next-status for each. The
// record endpoints mirror the backend guests/actions transitions:
//   send-invite  draft|invited       → invited   (resend self-loop)
//   confirm      draft|invited|pending|declined → confirmed (offline RSVP)
//   approve      pending|waitlisted  → confirmed (waitlist seat)
//   check-in     confirmed           → confirmed (+ checkedInAt)
//   check-out    checked-in          → confirmed (+ checkedOutAt)
//   cancel-invite (most non-terminal) → rescinded (destructive)
// `confirm`/`approve` stamp respondedAt; the optimistic patch only flips status
// (+ checkedInAt for check-in) — the cache invalidate refetches the rest.

export type GuestLifecycleId =
  | "send-invite"
  | "confirm"
  | "approve"
  | "check-in"
  | "check-out"
  | "cancel-check-in"
  | "cancel-check-out"
  | "cancel-invite"
  | "send-calendar-invite"
  | "cancel-calendar-invite"

export type GuestLifecycleSpec = {
  id: GuestLifecycleId
  /** Imperative button/menu label, status-aware (draft vs resend). */
  label: (g: Pick<Guest, "status">) => string
  /** Toast on success. */
  done: (g: Pick<Guest, "status">) => string
  /** Whether the action applies to a guest in this state. */
  enabled: (
    g: Pick<
      Guest,
      "status" | "checkedInAt" | "checkedOutAt" | "hostGuestId" | "sentAt" | "calendarInviteSentAt"
    >,
  ) => boolean
  /** Destructive → callers should confirm before running. */
  danger?: boolean
  /** Optimistic guest patch applied before the request resolves. */
  optimistic?: (g: Guest) => Partial<Guest>
}

export const GUEST_LIFECYCLE: Record<GuestLifecycleId, GuestLifecycleSpec> = {
  "send-invite": {
    id: "send-invite",
    label: (g) => (g.status === "draft" ? "Send invite" : "Resend invite"),
    done: (g) => (g.status === "draft" ? "Invite sent" : "Invite resent"),
    // Backend transition allows draft→invited (first send), the invited→invited
    // resend, and a status-PRESERVING resend to confirmed/waitlisted guests
    // (they lost the email — re-deliver the credential without resetting RSVP).
    // Offer it for pending too (organizer nudge); the action 409s for states it
    // can't leave and the toast surfaces that.
    enabled: (g) => ["draft", "invited", "pending", "confirmed", "waitlisted"].includes(g.status),
    // Only ADVANCE to invited from a not-yet-responded state; a resend to a
    // confirmed/waitlisted guest keeps their status (matches the backend).
    optimistic: (g) => (["confirmed", "waitlisted"].includes(g.status) ? {} : { status: "invited" }),
  },
  confirm: {
    id: "confirm",
    label: () => "Confirm",
    done: () => "Confirmed",
    // Offline RSVP path. Not for already-confirmed / checked-in / terminal.
    enabled: (g) => ["draft", "invited", "pending", "declined"].includes(g.status),
    optimistic: () => ({ status: "confirmed", preApproved: 1 }),
  },
  approve: {
    id: "approve",
    label: () => "Approve",
    done: () => "Approved",
    // Waitlist seat grant. A plus-one REQUEST (host-added, not yet invited)
    // must go through approve-plus-one instead — exclude it here.
    enabled: (g) =>
      ["pending", "waitlisted"].includes(g.status) && !(g.hostGuestId && !g.sentAt),
    optimistic: () => ({ status: "confirmed" }),
  },
  "check-in": {
    id: "check-in",
    label: () => "Check in",
    done: () => "Checked in",
    enabled: (g) => g.status === "confirmed" && !g.checkedInAt,
    optimistic: () => ({ checkedInAt: new Date().toISOString() }),
  },
  "check-out": {
    id: "check-out",
    label: () => "Check out",
    done: () => "Checked out",
    // Checked in (timestamp set) but not yet checked out. Status stays
    // "confirmed" through the whole check-in/out cycle.
    enabled: (g) => !!g.checkedInAt && !g.checkedOutAt,
    optimistic: () => ({ checkedOutAt: new Date().toISOString() }),
  },
  // Corrections — undo an accidental check-in/out. Clearing the check-in clears
  // the whole cycle; clearing the check-out reverts to checked-in.
  "cancel-check-in": {
    id: "cancel-check-in",
    label: () => "Clear check-in",
    done: () => "Check-in cleared",
    danger: true,
    enabled: (g) => !!g.checkedInAt,
    optimistic: () => ({ checkedInAt: null, checkedOutAt: null }),
  },
  "cancel-check-out": {
    id: "cancel-check-out",
    label: () => "Clear check-out",
    done: () => "Check-out cleared",
    danger: true,
    enabled: (g) => !!g.checkedOutAt,
    optimistic: () => ({ checkedOutAt: null }),
  },
  "cancel-invite": {
    id: "cancel-invite",
    label: () => "Cancel invite",
    done: () => "Invite cancelled",
    danger: true,
    enabled: (g) => !["rescinded", "cancelled", "declined"].includes(g.status),
    optimistic: () => ({ status: "rescinded" }),
  },
  // Calendar (ICS) invites — explicit, opt-in, and separate from the RSVP
  // invite. Each send goes out individually (one email/ATTENDEE per guest).
  "send-calendar-invite": {
    id: "send-calendar-invite",
    label: () => "Send calendar invite",
    done: () => "Calendar invite sent",
    // Offer for guests who are actually invited/attending. The backend also
    // requires an email; states it can't serve 409 with a surfaced toast.
    enabled: (g) => ["invited", "confirmed", "waitlisted"].includes(g.status),
    optimistic: () => ({ calendarInviteSentAt: new Date().toISOString() }),
  },
  "cancel-calendar-invite": {
    id: "cancel-calendar-invite",
    label: () => "Cancel calendar invite",
    done: () => "Calendar invite cancelled",
    danger: true,
    // Only meaningful once a calendar invite is actually outstanding.
    enabled: (g) => !!g.calendarInviteSentAt,
    optimistic: () => ({ calendarInviteSentAt: null }),
  },
}

/** The lifecycle actions a guest in this state can take, in menu order. */
export function guestLifecycleActions(
  g: Pick<
    Guest,
    "status" | "checkedInAt" | "checkedOutAt" | "hostGuestId" | "sentAt" | "calendarInviteSentAt"
  >,
): GuestLifecycleSpec[] {
  const order: GuestLifecycleId[] = [
    "send-invite",
    "approve",
    "confirm",
    "check-in",
    "check-out",
    "send-calendar-invite",
    "cancel-calendar-invite",
    "cancel-check-in",
    "cancel-check-out",
    "cancel-invite",
  ]
  return order.map((id) => GUEST_LIFECYCLE[id]).filter((s) => s.enabled(g))
}

// ── optimistic cache patching ────────────────────────────────────────────────
// Patch BOTH the event guests list (["resource","guests",{eventId}]) and the
// single guest record (["guest", id]) so the row + the open record update
// instantly; the realtime layer + a follow-up invalidate reconcile to truth.

export function patchGuestCaches(qc: QueryClient, guestId: string, patch: Partial<Guest>) {
  const apply = (rows: Guest[]) =>
    rows.map((r) => (r.id === guestId ? { ...r, ...patch } : r))
  // The list query caches the raw LIST ENVELOPE ({ data: Guest[] }) — its
  // `select` only unwraps it for components — so patch `.data`, not the value
  // directly. Tolerate a bare array too (other guests-prefixed caches).
  qc.setQueriesData({ queryKey: ["resource", "guests"] }, (data: unknown) => {
    if (Array.isArray(data)) return apply(data as Guest[])
    if (data && typeof data === "object" && Array.isArray((data as { data?: unknown }).data)) {
      const env = data as { data: Guest[] }
      return { ...env, data: apply(env.data) }
    }
    return data
  })
  qc.setQueryData<Guest | undefined>(guestKey(guestId), (g) =>
    g ? { ...g, ...patch } : g,
  )
}

export function invalidateGuestCaches(qc: QueryClient, eventId?: string | null, guestId?: string) {
  if (guestId) void qc.invalidateQueries({ queryKey: guestKey(guestId) })
  if (eventId) void qc.invalidateQueries({ queryKey: guestsKey(eventId) })
  void qc.invalidateQueries({ queryKey: ["resource", "guests"] })
}

/**
 * Run a single lifecycle action against a guest with an optimistic cache patch.
 * Rolls the patch back on failure (re-reading the snapshot), then invalidates so
 * the caches settle on server truth. Returns the action's JSON result.
 */
export async function runGuestLifecycle(
  qc: QueryClient,
  guest: Guest,
  spec: GuestLifecycleSpec,
  body: unknown = {},
): Promise<Record<string, unknown>> {
  const patch = spec.optimistic?.(guest)
  if (patch) patchGuestCaches(qc, guest.id, patch)
  try {
    const res = await guestAction(guest.id, spec.id, body)
    invalidateGuestCaches(qc, guest.eventId, guest.id)
    return res
  } catch (err) {
    // Roll back the optimistic fields to their pre-action values.
    if (patch) {
      const revert: Partial<Guest> = {}
      for (const k of Object.keys(patch) as (keyof Guest)[]) {
        ;(revert as Record<string, unknown>)[k] = guest[k]
      }
      patchGuestCaches(qc, guest.id, revert)
    }
    throw err
  }
}

export function actionErrorMessage(err: unknown, fallback = "Action failed"): string {
  if (err instanceof ApiError) return err.message || fallback
  if (err instanceof Error) return err.message || fallback
  return fallback
}

/**
 * Iterate a lifecycle action across many guests (per-guest granularity so one
 * 409 doesn't sink the batch), patching each optimistically. Returns ok/failed
 * counts plus the first error message for the toast.
 */
export async function runGuestLifecycleBulk(
  qc: QueryClient,
  guests: Guest[],
  spec: GuestLifecycleSpec,
  body: unknown = {},
): Promise<{ ok: number; failed: number; firstError?: string }> {
  const targets = guests.filter((g) => g.id && spec.enabled(g))
  if (targets.length === 0) return { ok: 0, failed: 0 }
  const results = await Promise.allSettled(
    targets.map((g) => runGuestLifecycle(qc, g, spec, body)),
  )
  let ok = 0
  let firstError: string | undefined
  for (const r of results) {
    if (r.status === "fulfilled") ok++
    else if (!firstError) firstError = actionErrorMessage(r.reason)
  }
  return { ok, failed: results.length - ok, firstError }
}

// One ticket per guest via the same endpoint the admin ComposeTicketDialog hits.
export type SendTicketResult = {
  success: boolean
  recipientCount?: number
  tickets?: Array<{ ticketId: string; guestUserId: string }>
  error?: string
}
export function sendGuestTicket(input: {
  eventId: string
  subject: string
  body: string
  userIds: string[]
}) {
  return api<SendTicketResult>("/api/v1/chat/send-to-support-tickets", {
    method: "POST",
    body: {
      eventId: input.eventId,
      subject: input.subject.trim(),
      body: input.body.trim(),
      recipientRule: { kind: "user_ids", userIds: input.userIds },
      dryRun: false,
    },
  })
}

// New-guest creation (mirrors admin AddGuestDialog → POST /api/v1/guests).
// NOTE: `sendInvite` is NOT a guests column — the create route rejects unknown
// fields (400 fieldNotCreateable). Sending the invite is a SECOND call to the
// send-invite action; see `createGuestAndMaybeInvite`.
export type CreateGuestInput = {
  eventId: string
  firstName: string
  lastName?: string
  email: string
  title?: string
  company?: string
}
export function createGuest(input: CreateGuestInput) {
  if (isV2Api) {
    return v2Api<{ success: true; person: V2EventPerson }>(
      `/api/v2/events/${encodeURIComponent(input.eventId)}/people`,
      {
        method: "POST",
        body: {
          idempotencyKey: crypto.randomUUID(),
          firstName: input.firstName.trim(),
          lastName: input.lastName?.trim() || undefined,
          displayName: joinName(input.firstName, input.lastName),
          email: input.email.trim(),
          title: input.title?.trim() || undefined,
          company: input.company?.trim() || undefined,
          roleKey: "attendee",
        },
      },
    ).then((response) => ({ id: response.person.id, person: response.person }))
  }
  return api<{ id?: string } & Record<string, unknown>>("/api/v1/guests", {
    method: "POST",
    body: {
      eventId: input.eventId,
      firstName: input.firstName.trim(),
      lastName: input.lastName?.trim() || undefined,
      nameAtInvite: joinName(input.firstName, input.lastName),
      emailAtInvite: input.email.trim(),
      titleAtInvite: input.title?.trim() || undefined,
      companyAtInvite: input.company?.trim() || undefined,
    },
  })
}

// Create a guest, then — when requested — fire the send-invite action against
// the freshly-created (draft) record so the invite email actually goes out.
// Returns the new guest id (when the API echoes it). Throws if the invite send
// fails AFTER a successful create, so the caller can surface "added, but invite
// failed" rather than silently claiming success.
export async function createGuestAndMaybeInvite(
  input: CreateGuestInput & { sendInvite?: boolean },
): Promise<{ id?: string; invited: boolean }> {
  if (isV2Api) {
    if (!input.sendInvite) {
      const created = await createGuest(input)
      return { id: typeof created.id === "string" ? created.id : undefined, invited: false }
    }
    const response = await issueEventInvitation({
      eventId: input.eventId,
      firstName: input.firstName.trim(),
      lastName: input.lastName?.trim() || undefined,
      displayName: joinName(input.firstName, input.lastName),
      email: input.email.trim(),
      title: input.title?.trim() || undefined,
      company: input.company?.trim() || undefined,
    })
    return { id: response.invitation.eventPersonId, invited: true }
  }
  const res = await createGuest(input)
  const id = typeof res?.id === "string" ? res.id : undefined
  if (input.sendInvite && id) {
    await guestAction(id, "send-invite")
    return { id, invited: true }
  }
  return { id, invited: false }
}

// ── display helpers ──────────────────────────────────────────────────────────

/** Badge / display name, falling back through legal name → invite name. */
export function guestDisplayName(g: Guest): string {
  const badge = joinName(g.badgeFirstName, g.badgeLastName)
  if (badge) return badge
  if (g.displayName) return g.displayName
  const legal = joinName(g.firstName, g.lastName)
  return legal || g.nameAtInvite || g.emailAtInvite || "Guest"
}

export function joinName(
  first?: string | null,
  last?: string | null,
): string {
  return [first, last].map((p) => (p ?? "").trim()).filter(Boolean).join(" ")
}

/** Buckets the many backend statuses into the four RSVP saved-view lanes. */
export type RsvpBucket = "confirmed" | "pending" | "declined" | "other"

export function rsvpBucket(status: string): RsvpBucket {
  switch (status) {
    case "confirmed":
    case "checked-in":
    case "checked-out":
      return "confirmed"
    case "draft":
    case "invited":
    case "pending":
    case "waitlisted":
      return "pending"
    case "declined":
    case "cancelled":
      return "declined"
    default:
      return "other"
  }
}

export function statusLabel(status: string): string {
  return status ? status.replace(/[-_]/g, " ").replace(/^./, (c) => c.toUpperCase()) : "—"
}

/** The status to SHOW. Check-in/out are tracked on checkedInAt / checkedOutAt
 *  while the stored `status` stays "confirmed", so a checked-in guest must read
 *  "Checked in" (and a checked-out one "Checked out"), not "Confirmed". */
export function displayStatus(
  g: Pick<Guest, "status" | "checkedInAt" | "checkedOutAt">,
): string {
  if (g.checkedOutAt) return "checked-out"
  if (g.checkedInAt) return "checked-in"
  return g.status
}

/** First name for friendly copy ("Open chat with Ada"). */
export function firstNameOf(name: string | null | undefined): string {
  if (!name) return "guest"
  return name.trim().split(/\s+/)[0] || "guest"
}

export function plusOneName(r: {
  displayName?: string | null
  firstName?: string | null
  lastName?: string | null
  nameAtInvite?: string | null
  emailAtInvite?: string
}): string {
  return (
    r.displayName?.trim() ||
    joinName(r.firstName, r.lastName) ||
    r.nameAtInvite?.trim() ||
    r.emailAtInvite ||
    "Guest"
  )
}

// ── form-response value rendering (ported from admin lib/form-response) ───────

// One entry in an `allergies`-field answer, mirroring guest_dietary.allergens.
export type AllergenAnswer = { kind?: string; label?: string | null; anaphylaxis?: boolean }

// Best-effort humanization of an allergen `kind` slug without the server
// taxonomy in hand (e.g. "tree_nut" → "Tree nut"). Catalog labels are richer
// where the taxonomy is loaded (AllergiesFieldInput); this is the fallback for
// table cells / CSV.
export function humanizeAllergenKind(kind: string): string {
  if (!kind) return ""
  return kind
    .split("_")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ")
}

export function isResponseField(field: FieldSpec): boolean {
  return !["section_heading", "paragraph", "divider"].includes(field.type)
}

export function formatResponseCell(field: FieldSpec, raw: unknown): string {
  if (raw == null) return "—"
  switch (field.type) {
    case "short_text":
    case "long_text":
    case "date":
      return typeof raw === "string" ? raw : "—"
    case "number":
      return typeof raw === "number" ? String(raw) : "—"
    case "boolean":
      return raw === true ? "Yes" : raw === false ? "No" : "—"
    case "rating":
      return typeof raw === "number" ? `${raw} / ${field.maxRating ?? 5}` : "—"
    case "single_select": {
      if (typeof raw !== "string") return "—"
      return (field.options ?? []).find((o) => o.value === raw)?.label ?? raw
    }
    case "multi_select": {
      if (!Array.isArray(raw)) return "—"
      const byValue = new Map((field.options ?? []).map((o) => [o.value, o.label] as const))
      return (raw as string[]).map((v) => byValue.get(v) ?? v).join(", ")
    }
    case "signature":
      return parseSignature(raw) ? "Signed" : "—"
    case "attachment": {
      const files = parseAttachments(raw)
      return files.length ? files.map((f) => f.filename).join(", ") : "—"
    }
    case "allergies": {
      if (!Array.isArray(raw)) return "—"
      const parts = (raw as AllergenAnswer[])
        .map((a) => {
          const name =
            a?.kind === "other"
              ? a.label?.trim() || "Other"
              : humanizeAllergenKind(a?.kind ?? "")
          if (!name) return ""
          return a?.anaphylaxis ? `${name} (severe)` : name
        })
        .filter(Boolean)
      return parts.length ? parts.join(", ") : "—"
    }
    case "section_heading":
    case "paragraph":
    case "divider":
      return "—"
    default:
      return typeof raw === "string" ? raw : JSON.stringify(raw)
  }
}

/** Format a stored custom attendee-field value (JSON-encoded) for display. */
export function formatAttendeeFieldValue(field: AttendeeField, raw: string | null): string {
  if (raw == null || raw === "") return "—"
  let value: unknown = raw
  try {
    value = JSON.parse(raw)
  } catch {
    /* keep raw string */
  }
  if (Array.isArray(value) && value.length === 0) return "—"
  return formatResponseCell(
    { id: field.id, type: field.type, label: field.label, options: field.options ?? undefined },
    value,
  )
}

// ── signature + attachment parsing (ported from admin lib) ───────────────────

export type SignaturePoint = [number, number]
export type SignaturePayload = { v: 1; strokes: SignaturePoint[][] }

const MAX_SIG_LEN = 100_000
const MAX_SIG_STROKES = 64
const MAX_SIG_POINTS = 8_192

export function parseSignature(raw: unknown): SignaturePayload | null {
  if (typeof raw !== "string" || raw.length === 0 || raw.length > MAX_SIG_LEN) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (!parsed || typeof parsed !== "object") return null
  const payload = parsed as { v?: unknown; strokes?: unknown }
  if (payload.v !== 1 || !Array.isArray(payload.strokes)) return null
  if (payload.strokes.length === 0 || payload.strokes.length > MAX_SIG_STROKES) return null
  let pointCount = 0
  const strokes: SignaturePoint[][] = []
  for (const rawStroke of payload.strokes) {
    if (!Array.isArray(rawStroke) || rawStroke.length === 0) return null
    pointCount += rawStroke.length
    if (pointCount > MAX_SIG_POINTS) return null
    const stroke: SignaturePoint[] = []
    for (const rawPoint of rawStroke) {
      if (!Array.isArray(rawPoint) || rawPoint.length !== 2) return null
      const [x, y] = rawPoint
      if (
        typeof x !== "number" ||
        typeof y !== "number" ||
        !Number.isFinite(x) ||
        !Number.isFinite(y) ||
        x < 0 ||
        x > 1 ||
        y < 0 ||
        y > 1
      ) {
        return null
      }
      stroke.push([x, y])
    }
    strokes.push(stroke)
  }
  return pointCount > 0 ? { v: 1, strokes } : null
}

export type FormAttachment = {
  mediaId: string
  url: string
  filename: string
  mimeType: string
  byteSize: number
  width?: number
  height?: number
}

export function parseAttachments(value: unknown): FormAttachment[] {
  if (typeof value !== "string" || value.length > 100_000) return []
  try {
    const payload = JSON.parse(value) as { v?: unknown; files?: unknown }
    if (payload.v !== 1 || !Array.isArray(payload.files)) return []
    return payload.files.filter((item): item is FormAttachment => {
      if (!item || typeof item !== "object") return false
      const f = item as Record<string, unknown>
      return (
        typeof f.mediaId === "string" &&
        typeof f.url === "string" &&
        /^https:\/\/files(?:-dev)?\.attend\.vip\//.test(f.url) &&
        typeof f.filename === "string" &&
        typeof f.mimeType === "string" &&
        typeof f.byteSize === "number"
      )
    })
  } catch {
    return []
  }
}
